import { useContext } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Button } from "@brainfinance/icash-component-library"

import { postMessageSender } from "../../../Helpers/PostMessage"
import { InstantLoanContext } from "../../../Helpers/Reducers/InstantLoan"

import warningIcon from "../../../images/identity-verification/warning.svg"
import cameraFailedIcon from "../../../images/identity-verification/camera-failed.svg"
import cancelIcon from "../../../images/identity-verification/cancel.svg"
import lightIcon from "../../../images/identity-verification/try-again/light.svg"
import pictureIcon from "../../../images/identity-verification/try-again/picture.svg"
import squareIcon from "../../../images/identity-verification/try-again/square.svg"
import thunderIcon from "../../../images/identity-verification/try-again/thunder.svg"
import synchIcon from "../../../images/identity-verification/try-again/synch.svg"
import infoIcon from "../../../images/info.svg"

type ErrorStatus = "session_expired" | "camera_access" | "unable_to_load" | "try_again" | "verification_incomplete"

interface LocationState {
  status?: ErrorStatus
  attemptNumber?: number
  maxRetryAttempts?: number
  title?: string
}

export function IdentityVerificationError() {
  const navigate = useNavigate()
  const { state } = useLocation()
  const locationState = state as LocationState | null

  const { state: { workflowType } } = useContext(InstantLoanContext)

  const status = locationState?.status ?? "session_expired"
  const attemptNumber = locationState?.attemptNumber ?? 1
  const maxRetryAttempts = locationState?.maxRetryAttempts ?? 3

  const handleRestartVerification = () => {
    navigate("/identity-verification", {
      state: { title: "Identity verification" }
    })
  }

  const handleTryAgain = () => {
    navigate("/identity-verification/verify", {
      state: { title: "Identity verification" }
    })
  }

  const handleOpenSettings = () => {
    postMessageSender({ event: "open-settings" })
  }

  const handleContactSupport = () => {
    postMessageSender({ event: "contact-us" })
  }

  const handleResumeVerification = () => {
    navigate("/identity-verification/verify", {
      state: { title: "Identity verification" }
    })
  }

  if (status === "verification_incomplete") {
    return (
      <div className="max-w-[622px] sm:w-full">
        <div className="flex justify-center mb-[32px] sm:mb-[22px]">
          <img src={warningIcon} alt="Verification incomplete" className="h-[70px] w-[70px]" />
        </div>

        <h2 className="title-caption !mb-[16px] !text-center">
          Verification incomplete
        </h2>

        <p className="caption-light !text-center !mb-[24px]">
          You haven't completed identity verification.<br/>This is required to process your loan application.
        </p>

        <div className="bg-[#FFCB3D4D] border border-[#FFCB3D] rounded-[8px] p-[16px] mb-[24px]">
          <div className="flex items-start flex-col">
            <div className="flex items-start flex-row gap-[12px] mb-[16px]">
              <span className="text-[18px]">⚠️</span>
              <p className="caption-light-no-color !text-[#13141A] !font-[600]">
                Time limit
              </p>
            </div>
            <p className="caption-medium">
              Your loan application is only open for a limited time. Please complete verification before it expires.
            </p>
          </div>
        </div>

        <Button
          size="large"
          appearance="primary"
          className="!w-full !mb-[16px]"
          onClick={handleResumeVerification}
        >
          Resume verification
        </Button>

        <Button
          size="large"
          appearance="transparent"
          className="!w-full !text-primary"
          onClick={handleContactSupport}
        >
          Contact support
        </Button>
      </div>
    )
  }

  if (status === "session_expired") {
    return (
      <div className="max-w-[622px] sm:w-full">
        <div className="flex justify-center mb-[32px] sm:mb-[22px]">
          <img src={warningIcon} alt="Session expired" className="h-[70px] w-[70px]" />
        </div>

        <h2 className="title-caption !mb-[16px] !text-center">
          Session expired
        </h2>

        <p className="caption-light !text-center !mb-[24px]">
          Your verification session has expired. Please start the verification process again.
        </p>

        <div className="bg-[#F7F7F7] rounded-[8px] p-[16px] mb-[24px]">
          <div className="flex items-start flex-col">
            <div className="flex items-start flex-row gap-[12px] mb-[16px]">
            <img src={infoIcon} alt="" className="w-[20px] h-[20px] flex-shrink-0 mt-[2px]" />
              <p className="caption-light-no-color !text-[#13141A] !font-[600]">
                Why did this happen?
              </p>
              </div>
              <p className="caption-medium">
                Sessions expire after a period of inactivity to protect your security.
              </p>
          </div>
        </div>

        <Button
          size="large"
          appearance="primary"
          className="!w-full"
          onClick={handleRestartVerification}
        >
          Restart verification
        </Button>
      </div>
    )
  }

  if (status === "camera_access") {
    return (
      <div className="max-w-[622px] sm:w-full">
        <div className="flex justify-center mb-[32px] sm:mb-[22px]">
          <img src={cameraFailedIcon} alt="Camera access required" className="h-[70px] w-[70px]" />
        </div>

        <h2 className="title-caption !mb-[16px] !text-center">
          Camera access required
        </h2>

        <p className="caption-light !text-center !mb-[24px]">
          Camera access is required for identity verification. Please enable camera access in your device settings.
        </p>

        <Button
          size="large"
          appearance="primary"
          className="!w-full !mb-[16px]"
          onClick={handleOpenSettings}
        >
          Open settings
        </Button>

        <Button
          size="large"
          appearance="transparent"
          className="!w-full !text-primary"
          onClick={handleContactSupport}
        >
          Contact support
        </Button>
      </div>
    )
  }

  if (status === "unable_to_load") {
    return (
      <div className="max-w-[622px] sm:w-full">
        <div className="flex justify-center mb-[32px] sm:mb-[22px]">
          <img src={cancelIcon} alt="Unable to load verification" className="h-[70px] w-[70px]" />
        </div>

        <h2 className="title-caption !mb-[16px] !text-center">
          Unable to load verification
        </h2>

        <p className="caption-light !text-center !mb-[24px]">
          We couldn't load the verification module.<br/>This could be due to a slow connection or an outdated app version.
        </p>

        <div className="bg-[#F7F7F7] rounded-[8px] p-[16px] mb-[24px]">
          <div className="flex items-start flex-col">
            <div className="flex items-start flex-row gap-[12px] mb-[16px]">
              <img src={infoIcon} alt="" className="w-[20px] h-[20px] flex-shrink-0 mt-[2px]" />
              <p className="caption-light-no-color !text-[#13141A] !font-[600]">
                What you can try
              </p>
            </div>
            <p className="caption-medium">
              Make sure your app is up to date, then try again. If the problem persists, contact support.
            </p>
          </div>
        </div>

        <Button
          size="large"
          appearance="primary"
          className="!w-full !mb-[16px]"
          onClick={handleTryAgain}
        >
          Try again
        </Button>

        <Button
          size="large"
          appearance="transparent"
          className="!w-full !text-primary"
          onClick={handleContactSupport}
        >
          Contact support
        </Button>
      </div>
    )
  }

  if (status === "try_again") {
    return (
      <div className="max-w-[622px] sm:w-full">
        <div className="flex justify-center mb-[32px] sm:mb-[22px]">
          <img src={warningIcon} alt="Let's try again" className="h-[70px] w-[70px]" />
        </div>

        <h2 className="title-caption !mb-[16px] !text-center">
          Let's try again
        </h2>

        <p className="caption-light !text-center !mb-[24px]">
          We had trouble processing your capture.<br/>Please follow the tips below and try again.
        </p>

        <div className="flex justify-center mb-[24px]">
          <div className="bg-[#FFF8E6] text-[#FF8B00] rounded-full px-[24px] py-[6px] flex items-center gap-[6px]">
            <img src={synchIcon} alt="" className="w-[16px] h-[16px]" />
            <span className="caption-medium-no-colors">Attempt {attemptNumber} of {maxRetryAttempts}</span>
          </div>
        </div>

        <div className="bg-[#F7F7F7] rounded-[8px] p-[16px] mb-[24px]">
          <div className="flex items-center gap-[12px] mb-[16px]">
            <img src={infoIcon} alt="" className="w-[20px] h-[20px] flex-shrink-0" />
            <p className="caption-light-no-color !text-[#13141A] !font-[600]">
              Tips for best results
            </p>
          </div>

          <div className="grid grid-cols-2 gap-[16px]">
            <div className="flex items-center gap-[12px]">
              <div className="bg-white rounded-full w-[32px] h-[32px] flex items-center justify-center flex-shrink-0">
                <img src={lightIcon} alt="" />
              </div>
              <span className="caption">Good lighting</span>
            </div>
            <div className="flex items-center gap-[12px]">
              <div className="bg-white rounded-full w-[32px] h-[32px] flex items-center justify-center flex-shrink-0">
                <img src={pictureIcon} alt="" />
              </div>
              <span className="caption">Hold steady</span>
            </div>
            <div className="flex items-center gap-[12px]">
              <div className="bg-white rounded-full w-[32px] h-[32px] flex items-center justify-center flex-shrink-0">
                <img src={squareIcon} alt="" />
              </div>
              <span className="caption">Fill the frame</span>
            </div>
            <div className="flex items-center gap-[12px]">
              <div className="bg-white rounded-full w-[32px] h-[32px] flex items-center justify-center flex-shrink-0">
                <img src={thunderIcon} alt="" />
              </div>
              <span className="caption">Avoid glare</span>
            </div>
          </div>
        </div>

        <Button
          size="large"
          appearance="primary"
          className="!w-full"
          onClick={handleTryAgain}
        >
          Try again
        </Button>
      </div>
    )
  }

  return null
}
